/**
 * Fetch + cache management for detent data
 */

import { useState, useCallback } from 'react';
import type { DetentData } from '../types';

interface DetentCacheState {
  data: Record<string, DetentData>;
  loading: Record<string, boolean>;
  errors: Record<string, string>;
}

export const useDetentCache = () => {
  const [state, setState] = useState<DetentCacheState>({
    data: {},
    loading: {},
    errors: {},
  });

  const fetchDetent = useCallback(async (detentId: string): Promise<DetentData | null> => {
    // Check if already cached
    if (state.data[detentId]) {
      return state.data[detentId];
    }

    // Check if already loading
    if (state.loading[detentId]) {
      return null;
    }

    // Set loading state
    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, [detentId]: true },
      errors: { ...prev.errors, [detentId]: '' },
    }));

    try {
      // Fetch from static data files
      // Determine if it's a year or zany detent
      const isYear = /^\d{4}$/.test(detentId);
      const path = isYear
        ? `/data/years/${detentId}.json`
        : `/data/zany/${detentId}.json`;

      const response = await fetch(path);

      if (!response.ok) {
        throw new Error(`Failed to fetch detent: ${response.statusText}`);
      }

      const blockData = await response.json();

      // Transform the block data into DetentData format
      const data: DetentData = {
        id: detentId,
        year: isYear ? parseInt(detentId) : 0,
        isZany: !isYear,
        blocks: [blockData] // Wrap the single block in an array
      };

      // Update cache
      setState(prev => ({
        ...prev,
        data: { ...prev.data, [detentId]: data },
        loading: { ...prev.loading, [detentId]: false },
      }));

      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, [detentId]: false },
        errors: { ...prev.errors, [detentId]: errorMessage },
      }));

      return null;
    }
  }, [state.data, state.loading]);

  const getDetent = useCallback((detentId: string): DetentData | null => {
    return state.data[detentId] || null;
  }, [state.data]);

  const isLoading = useCallback((detentId: string): boolean => {
    return state.loading[detentId] || false;
  }, [state.loading]);

  const getError = useCallback((detentId: string): string | null => {
    return state.errors[detentId] || null;
  }, [state.errors]);

  const clearCache = useCallback(() => {
    setState({
      data: {},
      loading: {},
      errors: {},
    });
  }, []);

  return {
    fetchDetent,
    getDetent,
    isLoading,
    getError,
    clearCache,
  };
};